import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import { getRecords } from './functions/index';

/**
 * EnhancedTable component
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.tableName - Name of the table
 * @param {string} props.searchEndpont - Endpoint for search
 * @param {any} props.searchAdditionalQueryParams - Additional query parameters for search
 * @param {function} props.actionRowClick - Callback function for row click action
 * @param {function} props.actionDelete - Callback function for delete action
 * @param {Array.<{id: string, dataType: string, label: string, defaultValue: (string|boolean)}>} props.filterList - List of filters
 * @param {Array.<{id: string, numeric: boolean, disablePadding: boolean, label: string, render: function, sortable: boolean}>} props.headCells - List of head cells
 * @param {number} [props.pageSize] - Number of rows per page (optional)
 * @param {Array.<number>} [props.pageSizeOptions] - Options for rows per page (optional)
 * @param {boolean} [props.selectableRows] - Flag to indicate if rows are selectable (optional)
 * @param {Object} [props.onAdd] - Add action object with label and onClick function (optional)
 * @param {string} props.onAdd.label - Label for the add action
 * @param {function} props.onAdd.onClick - Function to call when add is clicked
 * @param {React.ReactNode} [props.toolbar] - Custom toolbar component (optional)
 * @param {React.Ref} ref - Forwarded ref
 *
 * @returns {JSX.Element} EnhancedTable component
 */
// eslint-disable-next-line react/display-name
const EnhancedTable = React.forwardRef((props, ref) => {
	const theme = useTheme();

	let {
		filterList,
		actionRowClick,
		headCells,
		searchEndpont,
		searchAdditionalQueryParams,
		pageSize,
		pageSizeOptions = [5, 10, 25, 50, 100],
		selectableRows = true,
		options,
		sx = {},
		hybridMode = false,
		initialPageSize = 1000,
		onDataLoad,
		customNoRowsOverlay,
	} = props;

	const [allData, setAllData] = React.useState([]);
	const [totalCount, setTotalCount] = React.useState(0);
	const [loading, setLoading] = React.useState(false);
	const [dataLoaded, setDataLoaded] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	React.useEffect(() => {
		if (hybridMode && allData.length === 0 && !dataLoaded) {
			const loadCompleteDataset = async () => {
				setLoading(true);

				try {
					const countValues = {
						page: 0,
						pageSize: 1,
						filter: {},
						searchEndpont,
						searchAdditionalQueryParams,
						options: {},
						countOnly: true,
					};

					let totalRecords;
					try {
						const countResult = await getRecords(countValues);
						totalRecords =
							countResult.totalCount ||
							countResult.rows?.length ||
							initialPageSize;
					} catch (error) {
						console.log('Count query not supported, using fallback method');
						totalRecords = initialPageSize;
					}

					const maxSafeRecords = 10000;
					const recordsToLoad = Math.min(totalRecords, maxSafeRecords);

					if (totalRecords > maxSafeRecords) {
						console.warn(
							`Dataset has ${totalRecords} records. Loading ${maxSafeRecords} for memory safety.`
						);
					}

					const values = {
						page: 0,
						pageSize: recordsToLoad,
						filter: {},
						searchEndpont,
						searchAdditionalQueryParams,
						options: {}, // Don't pass search options for initial load
					};

					const result = await getRecords(values);
					if (result && result.rows) {
						setAllData(result.rows);
						setTotalCount(result.totalCount || result.rows.length);
						setDataLoaded(true);

						// Notify parent component about loaded data
						if (onDataLoad) {
							onDataLoad(result.rows);
						}
					}
				} catch (error) {
					const fallbackValues = {
						page: 0,
						pageSize: initialPageSize,
						filter: {},
						searchEndpont,
						searchAdditionalQueryParams,
						options: {},
					};
					const fallbackResult = await getRecords(fallbackValues);
					if (fallbackResult && fallbackResult.rows) {
						setAllData(fallbackResult.rows);
						setTotalCount(
							fallbackResult.totalCount || fallbackResult.rows.length
						);
						if (onDataLoad) {
							onDataLoad(fallbackResult.rows);
						}
					}
				}

				setLoading(false);
			};
			loadCompleteDataset();
		}
	}, [
		hybridMode,
		allData.length,
		initialPageSize,
		searchEndpont,
		searchAdditionalQueryParams,
		dataLoaded,
	]);

	const filteredData = React.useMemo(() => {
		if (!hybridMode || allData.length === 0) {
			return [];
		}

		let filtered = allData;

		const hasSearchFilter =
			options?.filter &&
			typeof options.filter === 'string' &&
			options.filter.trim();
		const hasCreatedByFilter = options?.createdBy;
		const hasStatusFilter = options?.status;
		const hasAssemblyTypeFilter =
			options?.assemblyType && options.assemblyType.trim();
		const hasEdgeTypeFilter = options?.edgeType && options.edgeType.trim();
		const hasOwnerFilter = options?.owner && options.owner.trim();
		const hasCategoryFilter = options?.category && options.category.trim();
		const hasArchiveFilter = options?.archive && options.archive.trim();
		const hasRefaceFilter = options?.reface && options.reface.trim();
		const hasRoleFilter = options?.role && options.role.trim();
		const hasCompanyFilter = options?.company && options.company.trim();

		if (
			!hasSearchFilter &&
			!hasCreatedByFilter &&
			!hasStatusFilter &&
			!hasAssemblyTypeFilter &&
			!hasEdgeTypeFilter &&
			!hasOwnerFilter &&
			!hasCategoryFilter &&
			!hasArchiveFilter &&
			!hasRefaceFilter &&
			!hasRoleFilter &&
			!hasCompanyFilter
		) {
			return allData;
		}

		const searchTerm = hasSearchFilter ? options.filter.toLowerCase() : null;
		const createdByTerm = hasCreatedByFilter
			? options.createdBy.toLowerCase()
			: null;

		// Single pass filtering for better performance
		filtered = allData.filter((row) => {
			if (searchTerm) {
				const searchableFields = [
					// Quotation fields
					row.projectName?.toLowerCase(),
					row.customerName?.toLowerCase(),
					row.companyName?.toLowerCase(),
					row.firstNameLastName?.toLowerCase(),
					row.status?.toLowerCase(),

					// Assembly fields
					row.name?.toLowerCase(),
					row.assemblyType?.name?.toLowerCase(),
					row.edgeType?.name?.toLowerCase(),

					// Company fields
					row.owner?.toLowerCase(),

					// Material fields
					row.obid?.toLowerCase(),
					row.category?.toLowerCase(),

					// Team member fields
					row.firstName?.toLowerCase(),
					row.lastName?.toLowerCase(),
					row.email?.toLowerCase(),
					row.role?.toLowerCase(),
					row.company?.toLowerCase(),

					// Permit fields
					row.permitName?.toLowerCase(),
					row.permitDescription?.toLowerCase(),
					row.permitAmount?.toString().toLowerCase(),

					// Additional Cost fields
					row.costName?.toLowerCase(),
					row.costDescription?.toLowerCase(),
					row.costAmount?.toString().toLowerCase(),
				].filter(Boolean); // Remove null/undefined values

				if (!searchableFields.some((field) => field.includes(searchTerm))) {
					return false;
				}
			}

			if (createdByTerm && row.firstNameLastName !== options.createdBy) {
				return false;
			}
			if (hasStatusFilter && row.status !== options.status) {
				return false;
			}

			// Assembly filters
			if (
				options?.assemblyType &&
				options.assemblyType.trim() !== '' &&
				row.assemblyType?.name !== options.assemblyType
			) {
				return false;
			}
			if (
				options?.edgeType &&
				options.edgeType.trim() !== '' &&
				row.edgeType?.name !== options.edgeType
			) {
				return false;
			}

			// Company filters
			if (
				options?.owner &&
				options.owner.trim() !== '' &&
				row.owner !== options.owner
			) {
				return false;
			}

			// Material filters
			if (
				options?.category &&
				options.category.trim() !== '' &&
				row.category !== options.category
			) {
				return false;
			}
			if (options?.archive && options.archive.trim() !== '') {
				const archiveStatus = row.archive ? 'Archived' : 'Active';
				if (archiveStatus !== options.archive) {
					return false;
				}
			}
			if (options?.reface && options.reface.trim() !== '') {
				const refaceStatus = row.reface ? 'Reface' : 'Regular';
				if (refaceStatus !== options.reface) {
					return false;
				}
			}

			// Team member filters
			if (
				options?.role &&
				options.role.trim() !== '' &&
				row.role !== options.role
			) {
				return false;
			}
			if (
				options?.company &&
				options.company.trim() !== '' &&
				row.company !== options.company
			) {
				return false;
			}

			return true;
		});

		return filtered;
	}, [
		hybridMode,
		allData,
		options?.filter,
		options?.createdBy,
		options?.status,
		options?.assemblyType,
		options?.edgeType,
		options?.owner,
		options?.category,
		options?.archive,
		options?.reface,
		options?.role,
		options?.company,
	]);

	const getRows = React.useCallback(
		async (params) => {
			if (hybridMode) {
				const { paginationModel } = params;
				const startIndex =
					(paginationModel?.page || 0) *
					(paginationModel?.pageSize || pageSizeOptions[0]);
				const endIndex =
					startIndex + (paginationModel?.pageSize || pageSizeOptions[0]);

				return Promise.resolve({
					rows: filteredData.slice(startIndex, endIndex),
					rowCount: filteredData.length,
				});
			} else {
				const { paginationModel, filterModel, sortModel } = params;
				const filter = {};
				if (filterList && filterModel && filterModel.items) {
					filterModel.items.forEach((item) => {
						if (item.value !== undefined && item.value !== '') {
							filter[item.field] = item.value;
						}
					});
				}
				const values = {
					page: paginationModel?.page || 0,
					pageSize: paginationModel?.pageSize || pageSizeOptions[0],
					filter,
					searchEndpont,
					searchAdditionalQueryParams,
					options,
					orderBy: sortModel?.[0]?.field,
					order: sortModel?.[0]?.sort,
				};
				setLoading(true);
				try {
					const result = await getRecords(values);
					return {
						rows: result.rows || [],
						rowCount: result.totalCount || 0,
					};
				} finally {
					setLoading(false);
				}
			}
		},
		[
			hybridMode,
			filteredData,
			filterList,
			searchEndpont,
			searchAdditionalQueryParams,
			options,
			pageSizeOptions,
			refreshKey,
		]
	);

	const dataSource = React.useMemo(() => ({ getRows }), [getRows]);

	React.useImperativeHandle(
		ref,
		() => ({
			onRefresh: () => {
				if (hybridMode) {
					setAllData([]);
					setDataLoaded(false);
				} else {
					setRefreshKey((prev) => prev + 1);
				}
			},
			updateData: (id, newData) => {
				setAllData((prev) =>
					prev.map((row) => (row._id === id ? { ...row, ...newData } : row))
				);
			},
		}),
		[hybridMode]
	);

	// Calculate height for 5 rows based on density
	const rowHeight = 52; // Default 'comfortable' density row height in MUI DataGrid
	const headerHeight = 56; // Default header height
	const totalHeight = headerHeight + rowHeight * 5;

	return (
		<DataGrid
			density="comfortable"
			columns={headCells}
			{...(hybridMode
				? {
						rows: filteredData,
						paginationMode: 'client',
						pagination: true,
					}
				: {
						dataSource: dataSource,
						pagination: true,
					})}
			initialState={{
				pagination: {
					paginationModel: {
						pageSize: pageSize || pageSizeOptions[0],
						page: 0,
					},
					rowCount: hybridMode ? filteredData.length : 0,
				},
			}}
			pageSizeOptions={pageSizeOptions}
			checkboxSelection={selectableRows}
			disableColumnMenu
			disableColumnResize
			disableMultipleColumnsFiltering={true}
			hideFooterSelectedRowCount
			disableRowSelectionOnClick
			getRowClassName={props.getRowClassName}
			onRowClick={(params, event) => {
				if (actionRowClick) {
					actionRowClick(params, event);
				}
			}}
			getRowId={(row) => row._id}
			loading={loading}
			slots={{
				toolbar: props.toolbar,
				noRowsOverlay: customNoRowsOverlay,
			}}
			showToolbar={props.toolbar !== undefined}
			slotProps={{
				loadingOverlay: {
					variant: 'skeleton',
					noRowsVariant: 'skeleton',
				},
			}}
			disableVirtualization={hybridMode}
			sx={{
				'&': {
					border: 'none',
					backgroundColor: theme.palette.background.paper,
					height: 'auto',
				},
				'.MuiDataGrid-columnsContainer': {
					backgroundColor: 'transparent',
					borderTopLeftRadius: theme.shape.borderRadius,
					borderTopRightRadius: theme.shape.borderRadius,
				},
				'.MuiDataGrid-columnHeaders': {
					border: 'none',
					backgroundColor: theme.palette.primary.light,
				},
				'.MuiDataGrid-row': {
					cursor: actionRowClick ? 'pointer' : 'default',
				},
				'& .MuiDataGrid-row:nth-of-type(even)': {
					backgroundColor: theme.palette.primary['100'],
				},
				'& .MuiDataGrid-cell': {
					wordWrap: 'break-word',
					overflow: 'unset',
					whiteSpace: 'normal',
					lineHeight: 1.35,
					py: 1,
				},
				'& .MuiDataGrid-cell:focus': {
					outline: 'none',
				},
				'& .MuiTablePagination-root': {
					maxHeight: 'none',
				},
				'--DataGrid-overlayHeight': totalHeight,
				'& .MuiDataGrid-footerContainer': {
					border: 'none',
					borderTop: `1px solid ${theme.palette.divider}`,
				},
				...sx,
			}}
		/>
	);
});

export default EnhancedTable;

EnhancedTable.propTypes = {
	tableName: PropTypes.string,
	searchEndpont: PropTypes.string,
	searchAdditionalQueryParams: PropTypes.any,
	actionRowClick: PropTypes.func,
	actionDelete: PropTypes.func,
	filterList: PropTypes.arrayOf(
		PropTypes.exact({
			id: PropTypes.string,
			dataType: PropTypes.string,
			label: PropTypes.string,
			defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
		})
	),
	headCells: PropTypes.arrayOf(PropTypes.object),
	pageSize: PropTypes.number,
	pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
	selectableRows: PropTypes.bool,
	options: PropTypes.shape(Object),
	toolbar: PropTypes.any,
	onAdd: PropTypes.shape({
		label: PropTypes.string,
		onClick: PropTypes.func,
	}),
	sx: PropTypes.object,
};
